import { useCallback, useMemo, useRef, useState } from 'react';
import { WorkoutGrid } from '@/components/WorkoutGrid';
import { Eyebrow, LinkButton } from '@/components/ui';
import type { GridRow, WorkoutLogApi } from '@/lib/useWorkoutLog';
import { DEMO_SESSION } from '@/data/demoSession';
import { GridIcon } from '@/lib/icons';
import type { SetDraft } from '@/app/grid';
import {
  PendingSyncNotice,
  SessionRuntimePanel,
  type RuntimeAbandonResult,
  type RuntimeCompletion,
  type RuntimeModel,
  type RuntimeMovement,
  type RuntimeSetState,
  type RuntimeTotals,
  type RuntimeWriteOutcome,
} from '@/app/session-runtime';

/**
 * Section 4 of the landing page: the fully interactive Grid. A visitor logs sets
 * before creating an account — the product is the pitch.
 *
 * Since the session panel is the real interface, the visitor can also run the
 * whole session: the same `SessionRuntimePanel`, driven by the example grid's
 * movements through the host-agnostic `RuntimeModel` + callbacks. Every write
 * stays inside the panel — no request, no account, nothing stored — and the
 * run-through closes with what the real thing does and a free sign-up.
 */

/** The panel's model, built from the throwaway grid rows. */
function demoRuntimeModel(session: typeof DEMO_SESSION, rows: GridRow[], totals: WorkoutLogApi['totals'], startedAt: string): RuntimeModel {
  return {
    sessionId: 'landing-demo',
    label: 'Demo — nothing is saved',
    startedAt,
    totals: {
      volumeKg: totals.volumeKg,
      totalReps: totals.totalReps,
      setsCompleted: totals.setsCompleted,
      setsPrescribed: totals.setsPrescribed,
      prCount: totals.prCount,
      durationMin: 0,
    },
    movements: rows.map((row) => ({
      id: row.id,
      exerciseId: row.exerciseId,
      name: row.name,
      prescription: {
        sets: row.prescribed.sets,
        repMin: row.prescribed.repMin,
        repMax: row.prescribed.repMax,
        targetRpe: row.prescribed.targetRpe,
        targetWeightKg: row.prescribed.targetWeightKg,
        restSeconds: row.prescribed.restSeconds,
        measure: row.measure,
        perSide: row.perSide,
        supersetGroup: row.supersetGroup,
        groupType: row.supersetGroup === null ? null : 'superset',
        tempo: row.tempo,
        notes: row.notes.length > 0 ? row.notes : null,
        isBodyweight: row.isBodyweight,
      },
      notes: row.notes.length > 0 ? row.notes : null,
      previous: [],
      sets: row.sets.map((set, index) => ({
        id: set.id,
        setNumber: index + 1,
        weightKg: set.weightKg,
        reps: set.reps,
        rpe: set.rpe,
        measure: set.measure,
        distanceM: set.distanceM,
        durationS: set.durationS,
        status: set.status,
        isPr: set.isPr,
      })),
    })),
  };
}

export function GridPreview({ api }: { api: WorkoutLogApi }) {
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  /** The demo's workout clock starts when the visitor does, not at page load. */
  const [demoStartedAt, setDemoStartedAt] = useState(() => new Date().toISOString());
  /** Everything the visitor logs in the demo panel — in memory, for the summary only. */
  const demoLogged = useRef<Map<string, { movement: RuntimeMovement; setNumber: number; draft: SetDraft }>>(new Map());

  const model = useMemo(
    () => demoRuntimeModel(DEMO_SESSION, api.rows, api.totals, demoStartedAt),
    [api.rows, api.totals, demoStartedAt],
  );

  const demoTotals = useCallback((): RuntimeTotals => {
    let volumeKg = 0;
    let totalReps = 0;
    let setsCompleted = 0;
    for (const movement of model.movements) {
      for (const set of movement.sets) {
        if (set.status === 'pending') continue;
        if (demoLogged.current.has(`${movement.id}:${set.setNumber}`)) continue;
        volumeKg += set.weightKg * set.reps;
        totalReps += set.reps;
        setsCompleted += 1;
      }
    }
    for (const entry of demoLogged.current.values()) {
      volumeKg += entry.draft.weightKg * entry.draft.reps;
      totalReps += entry.draft.reps;
      setsCompleted += 1;
    }
    return {
      volumeKg: Math.round(volumeKg * 100) / 100,
      totalReps,
      setsCompleted,
      setsPrescribed: model.totals?.setsPrescribed ?? null,
      prCount: 0,
      durationMin: 0,
    };
  }, [model]);

  const recordDemoSet = useCallback(
    (movement: RuntimeMovement, setNumber: number, draft: SetDraft): RuntimeWriteOutcome => {
      demoLogged.current.set(`${movement.id}:${setNumber}`, { movement, setNumber, draft });
      const set: RuntimeSetState = {
        id: `demo:${movement.id}:${setNumber}`,
        setNumber,
        weightKg: draft.weightKg,
        reps: draft.reps,
        rpe: draft.rpe,
        measure: movement.prescription.measure,
        distanceM: draft.distanceM,
        durationS: draft.durationS,
        status: 'done',
        isPr: false,
      };
      return { set, totals: demoTotals(), isPr: false };
    },
    [demoTotals],
  );

  const closeDemo = useCallback(() => {
    demoLogged.current.clear();
    setRunning(false);
    setFinished(false);
  }, []);

  return (
    <section id="grid" className="border-b border-hairline bg-page-subtle py-20">
      <div className="mx-auto max-w-[1200px] 2xl:max-w-[1440px] min-[1920px]:max-w-[1720px] px-4 sm:px-6">
        <div className="mb-10 flex flex-col gap-4">
          <Eyebrow>
            <GridIcon className="mr-1 inline text-sm" /> The Grid
          </Eyebrow>
          <h2 className="font-display text-h2 font-black uppercase leading-tight tracking-[-0.01em] text-ink md:text-display-2">
            A logging table that behaves like one.
          </h2>
          <p className="max-w-[70ch] text-body-lg text-ink-muted">
            This is the real component, running on a real mid-session state — not a screenshot. Click a cell, type,
            press <Kbd>Enter</Kbd>. Beat your previous best and the PR tag fires exactly the way the server-side trigger
            does, because it runs the same Epley maths and the same rep-range buckets.
          </p>
        </div>

        {/* The run-through: the real panel over the example grid, nothing stored. */}
        <div
          data-testid="demo-session-invite"
          className="mb-4 flex flex-wrap items-center justify-between gap-4 rounded-card border border-hairline border-l-4 border-l-accent bg-surface px-4 py-3.5"
        >
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-accent">Try the session panel</span>
            <span className="text-[13px] text-ink">
              Run this session exactly as it runs for real — countdown, rotator, rest, Done set — with no account and
              nothing saved.
            </span>
          </div>
          <button
            type="button"
            data-testid="demo-start-session"
            onClick={() => {
              demoLogged.current.clear();
              setDemoStartedAt(new Date().toISOString());
              setFinished(false);
              setRunning(true);
            }}
            className="inline-flex h-12 shrink-0 items-center gap-2 bg-accent px-6 text-[13px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover"
          >
            <span aria-hidden="true" className="text-[11px]">▶</span>
            Start session
          </button>
        </div>

        <WorkoutGrid api={api} session={DEMO_SESSION} />

        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {[
            {
              title: 'Add, edit, delete, undo',
              body: 'Every set is a cell you can edit in place. ⌘Z rewinds the last change, and the footer always shows what the keyboard can do.',
            },
            {
              title: 'Status tags that mean something',
              body: 'Done, PR, Below target and Failed are carried by a glyph and a word as well as colour — readable at arm’s length, mid-set.',
            },
            {
              title: 'Live totals, announced',
              body: 'Volume, sets completed against prescription, PRs and average RPE recompute on every keystroke inside an aria-live region.',
            },
          ].map((item) => (
            <div key={item.title} className="rounded-card border border-hairline bg-surface p-4">
              <h3 className="mb-1.5 text-[13px] font-bold uppercase tracking-[0.06em] text-ink">{item.title}</h3>
              <p className="text-[13px] leading-relaxed text-ink-muted">{item.body}</p>
            </div>
          ))}
        </div>
      </div>

      {running ? (
        <>
          <SessionRuntimePanel
            model={model}
            unit={api.unit}
            mode="countdown"
            onLogSet={async (movement, setNumber, draft) => recordDemoSet(movement, setNumber, draft)}
            onPatchSet={async (movement, setNumber, draft) => recordDemoSet(movement, setNumber, draft)}
            onComplete={async (): Promise<RuntimeCompletion | null> => {
              setFinished(true);
              return { totals: demoTotals() };
            }}
            onAbandon={async (): Promise<RuntimeAbandonResult> => {
              closeDemo();
              return { abandoned: true, supported: true, detail: 'Demo session closed — nothing was stored.' };
            }}
            onClose={closeDemo}
          />
          {/* The marketing moment: the run-through finished, so show what the real
              thing does and point at the free sign-up. Fixed above the panel. */}
          {finished ? (
            <div
              data-testid="demo-finish-cta"
              role="status"
              className="fixed inset-x-0 bottom-0 z-[97] flex flex-wrap items-center justify-center gap-x-4 gap-y-2 border-t border-hairline bg-surface px-4 py-3 text-center shadow-modal"
            >
              <span className="text-[13px] text-ink">
                That is the whole run-through. <span className="text-ink-muted">Nothing was saved.</span> The real panel
                writes every set to your account and reshapes next week.
              </span>
              <LinkButton href="#/signup" size="sm" onClick={closeDemo}>
                Start free — no card
              </LinkButton>
              <button
                type="button"
                data-testid="demo-finish-close"
                onClick={closeDemo}
                className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted hover:text-accent"
              >
                Close demo
              </button>
            </div>
          ) : (
            <div
              data-testid="demo-nothing-stored"
              className="sr-only"
              role="status"
            >
              Demo run-through — nothing is saved.
            </div>
          )}
          <PendingSyncNotice testId="demo-pending-sync" className="sr-only" />
        </>
      ) : null}
    </section>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded-sm border border-hairline bg-surface px-1.5 py-[1px] font-mono text-[11px] font-bold text-ink">
      {children}
    </kbd>
  );
}
